import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, MoreHorizontal, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { ChecklistItem  } from './ChecklistItem'
import { AddChecklistItemInput } from './AddChecklistItemInput'
import type {ChecklistItemReorderEvent} from './ChecklistItem';
import type { ChecklistDocument } from '@/lib/db/rxdb/hooks/entities/check-lists'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  useChecklistItemMutations,
  useChecklistItems,
  useChecklistMutations,
} from '@/lib/db/rxdb/hooks'
import { cn } from '@/lib/utils'
import {
  compareRanks,
  generateRankBetween,
} from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'

interface ChecklistCardProps {
  list: ChecklistDocument
  onMakeSubTask?: (content: string) => Promise<boolean>
  /**
   * The task's only checklist: its header is the section label itself,
   * "CHECKLIST 3/5" with a bar and + (design 28a). Otherwise each list keeps
   * its own title row under the section label.
   */
  flat?: boolean
  /** Flat header "+": add another checklist. */
  onAddChecklist?: () => void
}

const LONG_PRESS_MS = 500
const LONG_PRESS_MOVE_TOLERANCE_PX = 10

export function ChecklistCard({ list, onMakeSubTask, flat = false, onAddChecklist }: ChecklistCardProps) {
  const [isOpen, setIsOpen] = useState(true)
  const [isRenaming, setIsRenaming] = useState(false)
  const [titleDraft, setTitleDraft] = useState(list.title || '')
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{
    id: string
    position: 'before' | 'after'
  } | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const longPressTimerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const longPressStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const { checklistItems: items } = useChecklistItems({
    checklistId: list.id,
  })
  const { updateChecklistItem, deleteChecklistItem } =
    useChecklistItemMutations()
  const { updateChecklist, deleteChecklist } = useChecklistMutations()

  // Sync title draft when list.title changes externally
  useEffect(() => {
    if (!isRenaming) {
      setTitleDraft(list.title || '')
    }
  }, [list.title, isRenaming])

  // Auto-focus and select title input when entering rename mode
  useEffect(() => {
    if (isRenaming && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isRenaming])

  const handleRenameStart = useCallback(() => {
    setTitleDraft(list.title || '')
    setIsRenaming(true)
  }, [list.title])

  const handleRenameSave = useCallback(async () => {
    const trimmed = titleDraft.trim()
    setIsRenaming(false)

    // Save even if empty — will clear the title (shows default "Checklist")
    const newTitle = trimmed.length > 0 ? trimmed : null
    if (newTitle === list.title) return

    try {
      await updateChecklist(list.id, { title: newTitle })
    } catch {
      // Revert on error
      setTitleDraft(list.title || '')
    }
  }, [titleDraft, list.id, list.title, updateChecklist])

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void handleRenameSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setTitleDraft(list.title || '')
        setIsRenaming(false)
      }
    },
    [handleRenameSave, list.title],
  )

  // Items keep the order they were written in; ticking one doesn't move it (design 28a).
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? ''))
  }, [items])

  const completedCount = useMemo(() => {
    return items.filter((item) => item.isCompleted).length
  }, [items])

  const totalCount = items.length

  const handleReorder = useCallback(
    (event: ChecklistItemReorderEvent) => {
      const sourceIndex = sortedItems.findIndex((item) => item.id === event.sourceId)
      if (sourceIndex === -1) {
        return
      }

      const moved = sortedItems[sourceIndex]
      // Indexing gives `T | undefined` — bail rather than reordering against a
      // phantom item; this also lets every `moved.*` read below narrow.
      if (!moved) return
      const reordered = [...sortedItems]
      reordered.splice(sourceIndex, 1)

      const targetIndex = reordered.findIndex(
        (item) => item.id === event.targetId,
      )
      if (targetIndex === -1) {
        return
      }

      let insertIndex = targetIndex
      if (sourceIndex < targetIndex) {
        insertIndex -= 1
      }

      if (event.dropPosition === 'after') {
        insertIndex += 1
      }

      insertIndex = Math.max(0, Math.min(reordered.length, insertIndex))
      reordered.splice(insertIndex, 0, moved)

      const hasChanged = reordered.some(
        (item, index) => item.id !== sortedItems[index]?.id,
      )
      if (!hasChanged) {
        return
      }

      const movedIndex = reordered.findIndex((item) => item.id === moved.id)
      const beforeSort =
        movedIndex > 0 ? (reordered[movedIndex - 1]?.sortOrder ?? null) : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? (reordered[movedIndex + 1]?.sortOrder ?? null)
          : null

      const shouldRebalance =
        beforeSort !== null &&
        afterSort !== null &&
        compareRanks(beforeSort, afterSort) >= 0

      if (shouldRebalance) {
        void rebalanceSortOrders(reordered, updateChecklistItem)
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === moved.sortOrder) {
        return
      }

      try {
        void updateChecklistItem(moved.id, { sortOrder: nextSortOrder })
      } catch (error) {
        // Error handling
      }
    },
    [sortedItems, updateChecklistItem],
  )

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartPosRef.current = null
  }, [])

  useEffect(() => () => cancelLongPress(), [cancelLongPress])

  const handleTitlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== 'touch') return
      longPressTriggeredRef.current = false
      longPressStartPosRef.current = { x: e.clientX, y: e.clientY }
      cancelLongPress()
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTriggeredRef.current = true
        longPressTimerRef.current = null
        setMenuOpen(true)
      }, LONG_PRESS_MS)
    },
    [cancelLongPress],
  )

  const handleTitlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const start = longPressStartPosRef.current
      if (!start) return
      const dx = e.clientX - start.x
      const dy = e.clientY - start.y
      if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_TOLERANCE_PX) {
        cancelLongPress()
      }
    },
    [cancelLongPress],
  )

  const handleTitleClickCapture = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (longPressTriggeredRef.current) {
        e.preventDefault()
        e.stopPropagation()
        longPressTriggeredRef.current = false
      }
    },
    [],
  )

  const handleDelete = useCallback(async () => {
    try {
      // Delete the list (this will cascade to items in the backend)
      await deleteChecklist(list.id)

      // Also soft delete all items on the client
      const deletePromises = items.map((item) =>
        deleteChecklistItem(item.id),
      )
      await Promise.all(deletePromises)
    } catch (error) {
      // Error handling
    }
  }, [list.id, items, deleteChecklist, deleteChecklistItem])

  const percent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0
  const bar = (
    <span className="h-1 w-16 overflow-hidden rounded-full bg-secondary" aria-hidden>
      <span className="block h-full rounded-full bg-emerald-500 transition-[width]" style={{ width: `${percent}%` }} />
    </span>
  )

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      {/* Header */}
      {flat && !isRenaming ? (
        <div className="group/checklist flex items-center gap-2">
          <CollapsibleTrigger className="flex items-center gap-2 rounded text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground">
            {list.title && list.title !== 'Checklist' ? list.title : 'Checklist'}
            <span className="tabular-nums tracking-normal">
              {completedCount}/{totalCount}
            </span>
          </CollapsibleTrigger>
          {bar}
          <div className="ml-auto flex items-center gap-0.5">
            {onAddChecklist && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Add checklist"
                onClick={onAddChecklist}
                className="size-6 text-muted-foreground hover:text-foreground"
              >
                <Plus className="size-3.5" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Checklist options"
                  className="size-6 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/checklist:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                >
                  <MoreHorizontal className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleRenameStart}>
                  <Pencil className="mr-2 size-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void handleDelete()} className="text-destructive focus:text-destructive">
                  <Trash2 className="mr-2 size-4" />
                  Delete checklist
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : (
      <div className="flex items-center justify-between">
        {isRenaming ? (
          <div className="flex items-center gap-2 flex-1 min-w-0 px-1 -mx-1">
            <ChevronRight
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform',
                isOpen && 'rotate-90',
              )}
            />
            <input
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void handleRenameSave()}
              onKeyDown={handleRenameKeyDown}
              maxLength={200}
              placeholder="Checklist"
              className="flex-1 min-w-0 rounded border border-border bg-background px-2 py-0.5 text-base font-medium text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary md:text-sm"
            />
          </div>
        ) : (
          <CollapsibleTrigger
            className="flex items-center gap-2 flex-1 min-w-0 hover:bg-accent rounded px-1 -mx-1 cursor-pointer [-webkit-touch-callout:none]"
            onPointerDown={handleTitlePointerDown}
            onPointerMove={handleTitlePointerMove}
            onPointerUp={cancelLongPress}
            onPointerCancel={cancelLongPress}
            onPointerLeave={cancelLongPress}
            onClickCapture={handleTitleClickCapture}
            onContextMenu={(e) => e.preventDefault()}
          >
            <ChevronRight
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform',
                isOpen && 'rotate-90',
              )}
            />
            <span
              className="text-sm font-medium text-foreground truncate select-none"
              onDoubleClick={(e) => {
                e.stopPropagation()
                handleRenameStart()
              }}
            >
              {list.title || 'Checklist'}
            </span>
            <div className="flex items-center gap-2 shrink-0 ml-auto">
              {bar}
              <span className="text-xs tabular-nums text-muted-foreground select-none">
                {completedCount}/{totalCount}
              </span>
            </div>
          </CollapsibleTrigger>
        )}

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleRenameStart}>
              <Pencil className="mr-2 size-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleDelete()}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              Delete checklist
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}

      <CollapsibleContent className="space-y-1 pt-2">
        {/* Items List — pulled out by the row padding so checkboxes line up with the header */}
        <div className="-mx-2 space-y-0.5">
          {sortedItems.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              draggingId={draggingId}
              onDragStart={setDraggingId}
              onDragEnd={() => setDraggingId(null)}
              onReorder={handleReorder}
              dropIndicator={dropIndicator}
              onDropIndicatorChange={setDropIndicator}
              onMakeSubTask={onMakeSubTask}
            />
          ))}
        </div>

        {/* Add Item Input */}
        <div className="-mx-2 mt-2">
          <AddChecklistItemInput
            listId={list.id}
            envId={list.envId}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
